import React, { FC, ReactNode, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../assets/icons/index';
import Button, { ButtonDisableStyle } from '../button/index';

type DropDownProps = {
  answer: ReactNode | string;
  className?:
    | {
        answerLabel?: string;
        answerWrapper?: string;
        iconColor?: {
          closed: string;
          open: string;
        };
        questionIcon?: string;
        questionIconMonoColor?: boolean;
        questionLabel?: string;
        questionLabelWrapper?: string;
        wrapper?: string;
      }
    | string;
  defaultValue?: boolean;
  disabled?: boolean;
  handleExpandOnIconOnly?: boolean;
  onClick?: () => void;
  onStateChange?: (state: boolean) => void;
  question: ReactNode | string;
};
export const DropDown: FC<DropDownProps> = ({
  answer,
  question,
  className,
  disabled,
  onClick,
  handleExpandOnIconOnly,
  defaultValue = false,
  onStateChange,
}) => {
  const [isOpen, setOpen] = useState(defaultValue);

  const toggleState = () => {
    setOpen((prev) => {
      const next = !prev;
      onStateChange?.(next);
      return next;
    });
  };

  const handleState = (state: boolean) => {
    setOpen(state);
    onStateChange?.(state);
  };
  const isClassNameString = typeof className === 'string';

  const wrapperClassName = isClassNameString ? className : className?.wrapper;
  const questionLabelClassName = isClassNameString ? '' : className?.questionLabel;
  const questionLabelWrapperClassName = isClassNameString ? '' : className?.questionLabelWrapper;
  const answerLabelClassName = isClassNameString ? '' : className?.answerLabel;
  const answerWrapperClassName = isClassNameString ? '' : className?.answerWrapper;
  const questionIconClassName = isClassNameString ? '' : className?.questionIcon;
  const questionIconColor = isClassNameString
    ? { open: '#2565C8', closed: '#344054' }
    : className?.iconColor || { open: '#2565C8', closed: '#344054' };

  const questionIconMonoColor = isClassNameString ? false : className?.questionIconMonoColor;

  useEffect(() => {
    if (disabled) handleState(false);
  }, [disabled]);

  return (
    <div
      className={twMerge(
        'flex w-full cursor-pointer flex-col rounded-lg px-4 py-3 transition-all',
        wrapperClassName,
      )}
    >
      <Button
        className={twMerge(
          'flex w-full items-center justify-between',
          questionLabelWrapperClassName,
        )}
        disabledStyle={ButtonDisableStyle.NONE}
        onClick={() => {
          onClick?.();
          if (disabled || handleExpandOnIconOnly) return;
          toggleState();
        }}
        styleType="NONE"
      >
        <span className={twMerge('text-xs font-[450] text-gray-700', questionLabelClassName)}>
          {question}
        </span>
        {!disabled && (
          <div
            className="h-fit w-fit rounded-md p-1 transition-colors hover:bg-gray-50"
            onClick={() => {
              if (disabled || !handleExpandOnIconOnly) return;
              toggleState();
            }}
          >
            <ChevronDownIcon
              className={twMerge(
                'size-4',
                isOpen ? '!rotate-180' : '',
                'transition-transform',
                questionIconClassName,
              )}
              iconColor={
                questionIconMonoColor
                  ? '#2565C8'
                  : isOpen
                    ? questionIconColor.open
                    : questionIconColor.closed
              }
            />
          </div>
        )}
      </Button>
      <div
        className={twMerge(
          'grid grid-flow-row transition-all duration-300',
          isOpen ? 'mt-4 grid-rows-[1fr]' : 'grid-rows-[0fr]',
          answerWrapperClassName,
        )}
      >
        <span
          className={twMerge(
            'flex flex-col gap-5 overflow-hidden text-xs font-normal text-gray-700',
            answerLabelClassName,
          )}
        >
          {Array.isArray(answer)
            ? answer.map((paragraph, index) => <p key={index}>{paragraph}</p>)
            : answer}
        </span>
      </div>
    </div>
  );
};
